/**
 * modals/CriteriaItemPicker.tsx
 * Multi-select picker for criteria items
 *
 * Shows available criteria items grouped by category.
 * Allows multi-select and inline creation of new criteria items.
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Tag, MoreVertical, Pencil, Trash2 } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Listbox,
  ListboxItem,
  ListboxSection,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem
} from '@enfomentis/ui';

// 3. Relative imports
import type { CriteriaItemDraft, MarketConditionCategoryDraft, PlaybookDraft } from '../hooks/usePlaybookBuilder';
import { CriteriaItemModal } from './CriteriaItemModal';
import { DeleteCriteriaConfirmationModal } from './DeleteCriteriaConfirmationModal';

/**
 * Props for CriteriaItemPicker
 */
interface CriteriaItemPickerProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Available criteria items */
  criteriaItems: CriteriaItemDraft[];
  /** Market condition categories */
  marketConditionCategories: MarketConditionCategoryDraft[];
  /** Currently selected criteria IDs */
  selectedCriteriaIds: string[];
  /** All playbooks (for delete confirmation) */
  playbooks: PlaybookDraft[];
  /** Current playbook ID (for delete confirmation) */
  currentPlaybookId: string;
  /** Handler for selection change */
  onSelectionChange: (selectedIds: string[]) => void;
  /** Handler for adding new criteria item */
  onAddCriteriaItem: (criteriaData: { name: string; description?: string; categoryId?: string }) => string;
  /** Handler for creating a new category */
  onCreateCategory: (name: string, color: string, description?: string) => string;
  /** Handler for updating a criteria item */
  onUpdateCriteriaItem: (tempId: string, updates: { name?: string; description?: string; categoryId?: string }) => void;
  /** Handler for removing a criteria item */
  onRemoveCriteriaItem: (tempId: string) => void;
}

/**
 * Empty state component
 */
const EmptyState: React.FC<{ onAddNew: () => void }> = ({ onAddNew }) => (
  <div className="flex-1 flex items-center justify-center py-12">
    <div className="text-center max-w-md">
      <Tag className="w-16 h-16 text-text-secondary mx-auto mb-4" />
      <h4 className="text-lg font-medium text-text-primary mb-2">No Criteria Items Yet</h4>
      <p className="text-sm text-text-secondary mb-4">
        Create your first criteria item to define the conditions for your playbook.
      </p>
      <Button
        color="primary"
        startContent={<Plus className="w-4 h-4" />}
        onPress={onAddNew}
      >
        Create First Criteria
      </Button>
    </div>
  </div>
);

/**
 * Criteria item picker component
 */
export const CriteriaItemPicker: React.FC<CriteriaItemPickerProps> = ({
  isOpen,
  onClose,
  criteriaItems,
  marketConditionCategories,
  selectedCriteriaIds,
  playbooks,
  currentPlaybookId,
  onSelectionChange,
  onAddCriteriaItem,
  onCreateCategory,
  onUpdateCriteriaItem,
  onRemoveCriteriaItem
}) => {
  const [localSelectedKeys, setLocalSelectedKeys] = useState<Set<string>>(
    new Set(selectedCriteriaIds)
  );
  const [showCriteriaModal, setShowCriteriaModal] = useState(false);
  const [editingCriteria, setEditingCriteria] = useState<CriteriaItemDraft | undefined>(undefined);
  const [deletingCriteriaId, setDeletingCriteriaId] = useState<string | undefined>(undefined);

  // Update local selection when prop changes
  useEffect(() => {
    setLocalSelectedKeys(new Set(selectedCriteriaIds));
  }, [selectedCriteriaIds, isOpen]);

  /**
   * Handle opening add criteria modal
   */
  const handleAddNew = (): void => {
    setEditingCriteria(undefined);
    setShowCriteriaModal(true);
  };

  /**
   * Handle opening edit criteria modal
   */
  const handleEdit = (item: CriteriaItemDraft): void => {
    setEditingCriteria(item);
    setShowCriteriaModal(true);
  };

  /**
   * Handle saving new criteria item
   */
  const handleSaveCriteria = (
    criteriaData: { name: string; description?: string; categoryId?: string }
  ): string => {
    if (editingCriteria) {
      // Update existing criteria
      onUpdateCriteriaItem(editingCriteria.tempId, criteriaData);
      return editingCriteria.tempId;
    } else {
      // Add new criteria
      const tempId = onAddCriteriaItem(criteriaData);
      // Auto-select the newly created item
      const updatedSelection = new Set(localSelectedKeys);
      updatedSelection.add(tempId);
      setLocalSelectedKeys(updatedSelection);
      return tempId;
    }
  };

  /**
   * Handle opening delete confirmation
   */
  const handleDeleteClick = (tempId: string): void => {
    setDeletingCriteriaId(tempId);
  };

  /**
   * Handle apply selection
   */
  const handleApply = (): void => {
    onSelectionChange(Array.from(localSelectedKeys));
    onClose();
  };

  /**
   * Handle cancel
   */
  const handleCancel = (): void => {
    // Reset to original selection
    setLocalSelectedKeys(new Set(selectedCriteriaIds));
    onClose();
  };

  /**
   * Group criteria items by category
   */
  const groupedItems = useMemo(() => {
    const grouped = new Map<string, CriteriaItemDraft[]>();
    const uncategorized: CriteriaItemDraft[] = [];

    criteriaItems.forEach((item) => {
      if (item.categoryId) {
        const existing = grouped.get(item.categoryId) || [];
        grouped.set(item.categoryId, [...existing, item]);
      } else {
        uncategorized.push(item);
      }
    });

    return { grouped, uncategorized };
  }, [criteriaItems]);

  /**
   * Get category by ID
   */
  const getCategoryById = (categoryId: string): MarketConditionCategoryDraft | undefined => {
    return marketConditionCategories.find((cat) => cat.tempId === categoryId);
  };

  const hasItems = criteriaItems.length > 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onOpenChange={onClose}
        size="2xl"
        scrollBehavior="inside"
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex gap-2 items-center">
                <Tag className="w-5 h-5 text-primary" />
                <span>Select Criteria Items</span>
              </ModalHeader>

              <ModalBody>
                {!hasItems ? (
                  <EmptyState onAddNew={handleAddNew} />
                ) : (
                  <Listbox
                    aria-label="Criteria items"
                    selectionMode="multiple"
                    selectedKeys={localSelectedKeys}
                    onSelectionChange={(keys) => setLocalSelectedKeys(keys as Set<string>)}
                    variant="light"
                    classNames={{
                      base: "max-h-[400px] overflow-auto"
                    }}
                  >
                    {[
                      // Categorized items
                      ...Array.from(groupedItems.grouped.entries())
                        .sort(([aId], [bId]) => {
                          const catA = getCategoryById(aId);
                          const catB = getCategoryById(bId);
                          return (catA?.order || 0) - (catB?.order || 0);
                        })
                        .map(([categoryId, items]) => {
                          const category = getCategoryById(categoryId);
                          if (!category) return null;

                          return (
                            <ListboxSection
                              key={categoryId}
                              title={category.name}
                              classNames={{
                                heading: "text-sm font-semibold text-primary flex w-full border-b border-default-200",
                                base: "border border-default-200"
                              }}
                            >
                              {items.map((item) => (
                                <ListboxItem
                                  key={item.tempId}
                                  textValue={item.name}
                                  classNames={{
                                    base: "py-2",
                                    selectedIcon: "text-primary scale-150 order-first"
                                  }}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="font-medium">
                                        {item.name}
                                      </div>
                                      {item.description && (
                                        <div className="text-xs mt-0.5">
                                          {item.description}
                                        </div>
                                      )}
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <Dropdown>
                                        <DropdownTrigger>
                                          <Button
                                            isIconOnly
                                            size="sm"
                                            variant="light"
                                          >
                                            <MoreVertical className="w-4 h-4" />
                                          </Button>
                                        </DropdownTrigger>
                                      <DropdownMenu
                                        aria-label="Criteria actions"
                                        onAction={(key) => {
                                          if (key === 'edit') {
                                            handleEdit(item);
                                          } else if (key === 'delete') {
                                            handleDeleteClick(item.tempId);
                                          }
                                        }}
                                      >
                                        <DropdownItem key="edit" startContent={<Pencil className="w-4 h-4" />}>
                                          Edit
                                        </DropdownItem>
                                        <DropdownItem
                                          key="delete"
                                          color="danger"
                                          startContent={<Trash2 className="w-4 h-4" />}
                                        >
                                          Delete
                                        </DropdownItem>
                                      </DropdownMenu>
                                    </Dropdown>
                                    </div>
                                  </div>
                                </ListboxItem>
                              ))}
                            </ListboxSection>
                          );
                        })
                        .filter((section): section is React.ReactElement => section !== null),

                      // Uncategorized items
                      ...(groupedItems.uncategorized.length > 0
                        ? [
                            <ListboxSection
                              key="uncategorized"
                              title="Uncategorized"
                              classNames={{
                                heading: "text-sm font-semibold text-text-secondary"
                              }}
                            >
                              {groupedItems.uncategorized.map((item) => (
                                <ListboxItem
                                  key={item.tempId}
                                  textValue={item.name}
                                  classNames={{
                                    base: "py-2",
                                    selectedIcon: "text-primary scale-150 order-first"
                                  }}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex-1">
                                      <div className="font-medium text-text-primary">
                                        {item.name}
                                      </div>
                                      {item.description && (
                                        <div className="text-xs text-text-secondary mt-0.5">
                                          {item.description}
                                        </div>
                                      )}
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <Dropdown>
                                        <DropdownTrigger>
                                          <Button
                                            isIconOnly
                                            size="sm"
                                            variant="light"
                                          >
                                            <MoreVertical className="w-4 h-4" />
                                          </Button>
                                        </DropdownTrigger>
                                      <DropdownMenu
                                        aria-label="Criteria actions"
                                        onAction={(key) => {
                                          if (key === 'edit') {
                                            handleEdit(item);
                                          } else if (key === 'delete') {
                                            handleDeleteClick(item.tempId);
                                          }
                                        }}
                                      >
                                        <DropdownItem key="edit" startContent={<Pencil className="w-4 h-4" />}>
                                          Edit
                                        </DropdownItem>
                                        <DropdownItem
                                          key="delete"
                                          color="danger"
                                          startContent={<Trash2 className="w-4 h-4" />}
                                        >
                                          Delete
                                        </DropdownItem>
                                      </DropdownMenu>
                                    </Dropdown>
                                    </div>
                                  </div>
                                </ListboxItem>
                              ))}
                            </ListboxSection>
                          ]
                        : [])
                    ]}
                  </Listbox>
                )}
              </ModalBody>

              <ModalFooter className="flex justify-between">
                <Button
                  variant="bordered"
                  color="primary"
                  startContent={<Plus className="w-4 h-4" />}
                  onPress={handleAddNew}
                >
                  Criteria
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="light"
                    onPress={handleCancel}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    onPress={handleApply}
                  >
                    Save
                  </Button>
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Criteria Item Modal */}
      <CriteriaItemModal
        isOpen={showCriteriaModal}
        mode={editingCriteria ? 'edit' : 'add'}
        existingData={editingCriteria}
        marketConditionCategories={marketConditionCategories}
        onClose={() => {
          setShowCriteriaModal(false);
          setEditingCriteria(undefined);
        }}
        onSave={handleSaveCriteria}
        onCreateCategory={onCreateCategory}
      />

      {/* Delete Confirmation Modal */}
      {(() => {
        if (!deletingCriteriaId) return null;

        const criteriaItem = criteriaItems.find(item => item.tempId === deletingCriteriaId);
        if (!criteriaItem) return null;

        return (
          <DeleteCriteriaConfirmationModal
            isOpen={true}
            criteriaItem={criteriaItem}
            currentPlaybookId={currentPlaybookId}
            playbooks={playbooks}
            isSelectedInCurrentPlaybook={localSelectedKeys.has(deletingCriteriaId)}
            onClose={() => setDeletingCriteriaId(undefined)}
            onUnselect={() => {
              // Remove from current playbook selection only
              const updatedSelection = new Set(localSelectedKeys);
              updatedSelection.delete(deletingCriteriaId);
              setLocalSelectedKeys(updatedSelection);
            }}
            onDelete={() => {
              // Remove criteria item entirely
              onRemoveCriteriaItem(deletingCriteriaId);
              // Also remove from local selection if selected
              const updatedSelection = new Set(localSelectedKeys);
              updatedSelection.delete(deletingCriteriaId);
              setLocalSelectedKeys(updatedSelection);
            }}
          />
        );
      })()}
    </>
  );
};
